import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Filter, Map as MapIcon, List, Search, X } from 'lucide-react';
import Multiselect from 'multiselect-react-dropdown';

import { RadiusControl } from '../components/RadiusControl';
import { CityGroupRow } from '../components/CityGroupRow';
import { ScopeMap } from '../components/ScopeMap';
import { ButtonTemplate } from '../../../components/ui/buttons/ButtonTemplate';
import { useGetScopePreviewQuery } from '../api/parserImportApi';
import { useGetSpidersQuery } from '../../../redux/services/parser/parserApi';
import { AutoImportRuleModal } from '../components/AutoImportRuleModal';

const REALTY_TYPE_OPTIONS = [
  { title: 'Apartment', value: 0 },
  { title: 'Villa', value: 1 },
  { title: 'Land', value: 2 },
];

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));

// Step 1 — user chooses the scope: radius, cities, sources, price band.
export const ScopeStep = ({ flow, token, onCancel }) => {
  const [showMap, setShowMap] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);

  // Only the fields the scope-preview endpoint actually reads. Excluding
  // `cities` here is critical: cities toggles change the local selection
  // but do NOT change the scope-preview response — including them in the
  // query arg would change the RTK Query cache key on every checkbox
  // click, trigger a refetch, replace `data`, re-render the whole list,
  // and reset the scroll position.
  const scopeQueryFilters = useMemo(
    () => ({
      sources: flow.filters.sources,
      price_min: flow.filters.price_min,
      price_max: flow.filters.price_max,
      types: flow.filters.types,
      hide_imported: flow.filters.hide_imported,
    }),
    [
      flow.filters.sources,
      flow.filters.price_min,
      flow.filters.price_max,
      flow.filters.types,
      flow.filters.hide_imported,
    ]
  );

  const { data, isLoading, error, isFetching } = useGetScopePreviewQuery({
    token,
    radius_km: flow.radiusKm,
    filters: scopeQueryFilters,
  });

  const groups = data?.groups ?? [];
  const total = data?.total_records ?? 0;
  const alreadyImported = data?.already_imported_count ?? 0;

  const selectedCities = new Set(flow.filters.cities);

  // Scope-wide totals vs. filtered-by-selection totals. When the user has
  // picked one or more city groups, the Continue button should show the
  // count for JUST those groups — otherwise it defaults to everything in
  // radius (which is what the backend will import if `cities` is empty).
  const selectionTotals = useMemo(() => {
    if (selectedCities.size === 0) {
      return { total, alreadyImported };
    }
    const picked = groups.filter((g) => selectedCities.has(g.city));
    return {
      total: picked.reduce((n, g) => n + (g.total || 0), 0),
      alreadyImported: picked.reduce((n, g) => n + (g.already_imported || 0), 0),
    };
  }, [groups, flow.filters.cities, total, alreadyImported]);

  const willImport = selectionTotals.total - selectionTotals.alreadyImported;

  // Default sort: by count of unimported records (total - already_imported),
  // descending — so the cities with the most work to do bubble to the top.
  const sortedGroups = useMemo(() => {
    const remaining = (g) =>
      Math.max(0, (g.total || 0) - (g.already_imported || 0));
    return [...groups].sort((a, b) => remaining(b) - remaining(a));
  }, [groups]);

  const visibleGroups = useMemo(() => {
    const q = cityFilter.trim().toLowerCase();
    if (!q) return sortedGroups;
    return sortedGroups.filter((g) => (g.city || '').toLowerCase().includes(q));
  }, [sortedGroups, cityFilter]);

  const visibleTotal = visibleGroups.reduce((n, g) => n + (g.total || 0), 0);
  const isFilterActive = cityFilter.trim() !== '';

  const toggleCity = (city) => {
    const next = new Set(selectedCities);
    if (next.has(city)) next.delete(city);
    else next.add(city);
    flow.setFilters({ cities: [...next] });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedCities);
    visibleGroups.forEach((g) => next.add(g.city));
    flow.setFilters({ cities: [...next] });
  };
  const deselectAllVisible = () => {
    const next = new Set(selectedCities);
    visibleGroups.forEach((g) => next.delete(g.city));
    flow.setFilters({ cities: [...next] });
  };

  const canContinue = selectionTotals.total > 0 && !isLoading && !error;

  const priceMin = flow.filters.price_min ?? '';
  const priceMax = flow.filters.price_max ?? '';

  const { data: spidersData } = useGetSpidersQuery({ token });
  const spiderOptions = useMemo(
    () => (spidersData?.data || []).map((s) => ({ title: s, value: s })),
    [spidersData]
  );
  const selectedSources = (flow.filters.sources || []).map((v) => ({ title: v, value: v }));
  const selectedTypes = (flow.filters.types || [])
    .map((v) => REALTY_TYPE_OPTIONS.find((o) => o.value === v))
    .filter(Boolean);

  const summary = useMemo(() => {
    if (isLoading) return 'Computing scope…';
    if (error) return 'Failed to load scope';
    const scopeSuffix = selectedCities.size > 0
      ? ` (from ${selectedCities.size} of ${groups.length} cities)`
      : ` in ${flow.radiusKm} km`;
    return `${fmt(willImport)} new · ${fmt(selectionTotals.alreadyImported)} already imported · ${fmt(selectionTotals.total)} total${scopeSuffix}`;
  }, [isLoading, error, willImport, selectionTotals, flow.radiusKm, selectedCities, groups.length]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <RadiusControl value={flow.radiusKm} onChange={flow.setRadius} />

      <div className="flex items-center justify-between w-full">
        <span
          data-testid="scope-available-count"
          className={
            'text-sm ' + (error ? 'text-redColor' : 'text-blackColor font-medium')
          }
        >
          {summary}
          {isFetching && !isLoading && (
            <span className="ml-2 text-xs text-darkGrayColor animate-pulse">
              refreshing…
            </span>
          )}
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAdvanced((s) => !s)}
            className="flex items-center gap-1 text-xs text-darkGrayColor hover:text-blackColor"
          >
            <Filter size={14} />
            Advanced filters
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <button
            type="button"
            onClick={() => setShowMap((s) => !s)}
            className="flex items-center gap-1 text-xs text-darkGrayColor hover:text-blackColor"
          >
            {showMap ? <List size={14} /> : <MapIcon size={14} />}
            {showMap ? 'List' : 'Map'}
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded border border-gray-200 bg-lightestGrayColor">
          <div className="flex flex-col gap-1 md:col-span-2" data-testid="scope-filter-source">
            <label className="text-xs text-darkGrayColor">Sources (spiders)</label>
            <Multiselect
              displayValue="title"
              placeholder="All sources"
              options={spiderOptions}
              selectedValues={selectedSources}
              showCheckbox={true}
              closeOnSelect={false}
              onSelect={(list) => flow.setFilters({ sources: list.map((o) => o.value) })}
              onRemove={(list) => flow.setFilters({ sources: list.map((o) => o.value) })}
            />
          </div>
          <div className="flex flex-col gap-1" data-testid="scope-filter-type">
            <label className="text-xs text-darkGrayColor">Realty type</label>
            <Multiselect
              displayValue="title"
              placeholder="All types"
              options={REALTY_TYPE_OPTIONS}
              selectedValues={selectedTypes}
              showCheckbox={true}
              closeOnSelect={false}
              onSelect={(list) => flow.setFilters({ types: list.map((o) => o.value) })}
              onRemove={(list) => flow.setFilters({ types: list.map((o) => o.value) })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-darkGrayColor">Min price (€)</label>
            <input
              data-testid="scope-filter-price-min"
              type="number"
              value={priceMin}
              onChange={(e) =>
                flow.setFilters({
                  price_min: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              placeholder="0"
              className="px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-darkGrayColor">Max price (€)</label>
            <input
              data-testid="scope-filter-price-max"
              type="number"
              value={priceMax}
              onChange={(e) =>
                flow.setFilters({
                  price_max: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              placeholder="No limit"
              className="px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-blackColor cursor-pointer">
              <input
                data-testid="scope-filter-hide-imported"
                type="checkbox"
                checked={!!flow.filters.hide_imported}
                onChange={(e) =>
                  flow.setFilters({ hide_imported: e.target.checked })
                }
                className="accent-purpleColor"
              />
              Hide already-imported
            </label>
          </div>
        </div>
      )}

      {showMap ? (
        <ScopeMap radiusKm={flow.radiusKm} groups={groups} />
      ) : (
        <div className="w-full max-h-[42vh] overflow-y-auto pr-1">
          {isLoading && (
            <ul className="flex flex-col gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <li
                  key={i}
                  className="h-16 rounded border border-gray-200 bg-lightGrayColor animate-pulse"
                />
              ))}
            </ul>
          )}

          {!isLoading && error && (
            <div className="text-sm text-redColor py-4">
              Failed to load scope preview. Please retry.
            </div>
          )}

          {!isLoading && !error && groups.length === 0 && (
            <div className="text-sm text-darkGrayColor py-4">
              No records in this radius. Try increasing the radius or clearing filters.
            </div>
          )}

          {!isLoading && !error && groups.length > 0 && (
            <>
              <div className="sticky top-0 z-10 bg-white pb-2 mb-2 border-b border-gray-100">
                <div className="relative mb-2">
                  <Search
                    size={14}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-darkGrayColor pointer-events-none"
                  />
                  <input
                    data-testid="scope-city-search"
                    type="text"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    placeholder='Search cities — e.g. type "nice" to match "Nice", "Nice Cimiez", "Nice - Fabron"…'
                    className="w-full pl-7 pr-8 py-1.5 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
                  />
                  {cityFilter && (
                    <button
                      type="button"
                      onClick={() => setCityFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-darkGrayColor hover:text-blackColor"
                      aria-label="Clear city search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-darkGrayColor">
                    {isFilterActive
                      ? `${visibleGroups.length} matching / ${groups.length} cities · ${fmt(visibleTotal)} records`
                      : `${selectedCities.size} of ${groups.length} cities selected`}
                  </span>
                  <div className="flex items-center gap-3 text-xs">
                    <button
                      type="button"
                      className="text-purpleColor hover:underline disabled:text-lightGrayColor disabled:no-underline"
                      onClick={selectAllVisible}
                      disabled={visibleGroups.length === 0}
                    >
                      {isFilterActive ? `Select all matching (${visibleGroups.length})` : 'Select all'}
                    </button>
                    <button
                      type="button"
                      className="text-darkGrayColor hover:underline disabled:text-lightGrayColor disabled:no-underline"
                      onClick={deselectAllVisible}
                      disabled={visibleGroups.length === 0}
                    >
                      {isFilterActive ? 'Clear matching' : 'Clear'}
                    </button>
                  </div>
                </div>
              </div>

              {visibleGroups.length === 0 ? (
                <div className="text-sm text-darkGrayColor py-4">
                  No cities match “{cityFilter}”. Try a shorter query.
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {visibleGroups.map((g) => (
                    <CityGroupRow
                      key={g.city}
                      group={g}
                      isSelected={selectedCities.has(g.city)}
                      onToggle={toggleCity}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 w-full pt-3 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-darkGrayColor rounded border border-gray-200 hover:bg-lightGrayColor"
        >
          Cancel
        </button>
        <button type="button" onClick={() => setIsRuleModalOpen(true)} className="px-3 py-1.5 text-sm text-purpleColor rounded border border-purpleColor hover:bg-purpleColor/5">
          Save as auto-rule
        </button>
        <span data-testid="scope-preview-submit">
          <ButtonTemplate
            text={
              canContinue
                ? `Continue with ${fmt(willImport)} records →`
                : 'Continue'
            }
            bgColor="bg-purpleColor border-purpleColor"
            disabled={!canContinue}
            handleClick={() => flow.next()}
          />
        </span>
      </div>
      {isRuleModalOpen && <AutoImportRuleModal isOpen={isRuleModalOpen} onClose={() => setIsRuleModalOpen(false)} token={token} initialRadius={flow.radiusKm} initialFilters={flow.filters} />}
    </div>
  );
};
